import { useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import type { PedidoVista } from '@shared/types';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 76 — Despachar es **confirmar**, no volver a teclear.
 *
 * Del grupo MOTO MANDADO vuelven dos datos —cuánto cobran y quién viene— y desde ese sprint los
 * dos se capturan al cotizar, media hora antes de que la moto llegue. Este modal ya sólo
 * confirma: un clic contra ocho letras, con el repartidor esperando en la puerta.
 *
 * Que venga otro no es una incidencia. El negocio fue explícito: *"sí [ha pasado], pero si es la
 * misma tarifa no hay problema"*. Así que cambiar de repartidor es escribir el nombre y seguir —
 * sin PIN, sin motivo obligatorio, sin alerta roja. El nombre real se registra igual, porque
 * cuando un paquete no llega la primera pregunta es "¿quién se lo llevó?".
 *
 * ## Sprint 99 — Sale de `DespachoPage` para vivir donde se usa
 *
 * Estaba declarado dentro de la pantalla de Despacho, así que el tablero de A domicilio —que ya
 * modela la columna "Listo · esperando moto"— no podía usarlo: su botón "Entregar a la moto"
 * **navegaba a otra pantalla**. Aquí no cambia ni una regla; sólo deja de estar encerrado.
 */
export function AsignarRepartidorModal({
  pedido,
  onClose,
  onAsignado,
}: {
  pedido: PedidoVista | null;
  onClose: () => void;
  onAsignado: (pedido: PedidoVista, pdfPath: string | null) => void;
}) {
  const previsto = (pedido?.nombreRepartidor ?? '').trim();
  const [nombre, setNombre] = useState('');
  const [cambiando, setCambiando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (pedido) {
      setNombre((pedido.nombreRepartidor ?? '').trim());
      setCambiando(!pedido.nombreRepartidor);
      setError(null);
    }
  }, [pedido]);

  if (!pedido) return null;

  const enviar = async (nombreFinal: string) => {
    if (nombreFinal.trim().length < 2) {
      setError('Escribe quién se lo lleva: sin nombre no queda registro de con quién salió.');
      return;
    }
    setEnviando(true);
    setError(null);
    const r = await window.api.pedidos.asignarRepartidor({
      pedidoId: pedido.id,
      nombreRepartidor: nombreFinal.trim(),
    });
    setEnviando(false);
    if (!r.ok) {
      setError(traducirErrorBackend(r.error));
      return;
    }
    onAsignado(r.data.pedido, r.data.pdfPath);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="card w-full max-w-sm">
        <h2 className="text-lg font-bold mb-1">Entregar a la moto</h2>
        <p className="text-xs text-slate-500 mb-3">
          Pedido #{String(pedido.numeroPedido).padStart(5, '0')} — {pedido.clienteNombre}
        </p>

        {previsto && !cambiando ? (
          <>
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              🏍 Cotizó <b>{previsto}</b>
            </div>
            <div className="mt-3 space-y-2">
              <button
                onClick={() => enviar(previsto)}
                className="btn-primary w-full min-h-[44px] bg-emerald-600 hover:bg-emerald-700"
                disabled={enviando}
              >
                <Printer className="h-4 w-4" />
                {enviando ? 'Generando…' : `Sí, vino ${previsto} · Entregar e imprimir`}
              </button>
              <button
                onClick={() => {
                  setCambiando(true);
                  setNombre('');
                }}
                className="btn-ghost w-full text-sm"
                disabled={enviando}
              >
                Vino otro repartidor…
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="text-sm font-medium">¿Quién se lo lleva?</label>
            <input
              autoFocus
              className="input w-full mt-1"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="Ej: Juan, El güero, Yo misma"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !enviando) enviar(nombre);
              }}
            />
            {previsto && (
              <p className="mt-1 text-xs text-slate-500">
                Se había anotado <b>{previsto}</b>. Si cobra la misma tarifa, no hay nada más que
                hacer: queda registrado quién se lo llevó de verdad.
              </p>
            )}
            <button
              onClick={() => enviar(nombre)}
              className="btn-primary w-full min-h-[44px] mt-3 bg-emerald-600 hover:bg-emerald-700"
              disabled={enviando}
            >
              <Printer className="h-4 w-4" />
              {enviando ? 'Generando…' : 'Despachar e imprimir'}
            </button>
          </>
        )}

        {error && <div className="text-rose-700 text-sm mt-2">{error}</div>}
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} className="btn-ghost" disabled={enviando}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
